/**
 * types.ts — Shared enums and the Game→HUD event contract.
 *
 * Keeping these in one place avoids circular imports between the game core,
 * entities, and the HUD, and gives a single list of every event the HUD reacts to.
 */

/** The dog's behavioural state. Drives both its AI target and the leash pull. */
export enum DogState {
  CALM = 'CALM',
  DISTRACTED = 'DISTRACTED',
  HYPER_PULL = 'HYPER_PULL',
  /**
   * Circling and nosing at the ground — the tell that the urge is nearly here.
   *
   * The poop used to arrive with no warning at all: the first the player knew
   * of it was the dog suddenly hauling them somewhere. This is the few seconds
   * before that, and it exists purely so the walk to the park is something you
   * can start early rather than something that happens to you. It grants
   * nothing on its own; `DOG_STATE.SNIFF_WARNING_MS` is how long it lasts.
   */
  SNIFFING = 'SNIFFING',
  /**
   * Needs to go, and is towing you toward the nearest park to do it.
   *
   * A dog will only do its business on grass, so the urge and the act are two
   * different states now: this one is the walk to the green, and it can last as
   * long as the player takes to get there. See `systems/grass.ts`.
   */
  NEEDS_GRASS = 'NEEDS_GRASS',
  /**
   * Going, right now — a squat that lasts `DOG_STATE.POOP_DURATION_MS`.
   *
   * The act used to be a single frame: the poop simply appeared under a dog
   * that had visibly done nothing, and the first the player knew of it was the
   * objective line changing. Now the dog crouches, a ring fills around it, and
   * the poop lands when that ring is full.
   */
  POOPING = 'POOPING',
  /**
   * Has gone, and is rooted while the walker bags it — `OBJECTIVE.SCOOP_MS`.
   *
   * This used to be open-ended: the dog stood over the mess until the player
   * held a button down for three seconds. See {@link Objective} for why that
   * step is gone; what's left is the beat of the walker bending down, which the
   * dog has no reason to wander off in the middle of.
   */
  POOP_PENDING = 'POOP_PENDING',
  CLEANED = 'CLEANED',
}

/**
 * High-level objective progression for the whole level.
 *
 * There used to be a `CLEANUP_PENDING` step between the first two: the dog
 * went, and the player then had to stand over the mess holding Space (or an
 * on-screen button) for three seconds to pick it up. It's gone, because it was
 * never a decision. The dog is an anchor while it's going, so the walker is
 * hard-clamped within a lead's length of the mess — they were always in range,
 * and the only thing the step asked for was three seconds of holding a button
 * they pressed nowhere else in the game, on top of the three they had just
 * spent watching the squat. Going and bagging are one beat now: the dog
 * finishes, the walker bends down, and the bag is in hand. What remains of the
 * errand is the part that was always the game — carrying it to a bin.
 */
export enum Objective {
  WALKING = 'WALKING', // free roam; the dog goes and is bagged during this phase
  CARRYING_BAG = 'CARRYING_BAG', // the dog has been and bagged; take it to the bin
  GO_HOME = 'GO_HOME', // bag binned; home zone revealed
  WON = 'WON', // reached home
}

/** Hazard flavours. Every one of them sets the dog off; see systems/frenzy.ts. */
export type HazardType = 'cat' | 'fox' | 'squirrel' | 'pigeon';

/**
 * What a critter does about the dog — the one thing that tells the four species
 * apart once they are all on screen.
 *
 * They all wander the same way and they all wind the dog up the same way. What
 * a player can actually *learn* is what happens when the dog reaches one, and
 * that is a different answer for each:
 *
 * - `flee`  bolt away the moment the dog is near, and keep going. The squirrel.
 * - `hold`  stand your ground: ears back, frozen, but it does not give an inch.
 *   The fox, which has no reason to be afraid of a dog on a lead.
 * - `nerve` hold like the fox, *until* the dog closes inside
 *   `HAZARD.PANIC_RADIUS` — then break and run. The cat, and the only one of
 *   the four whose answer changes with how close you get.
 * - `flush` unbothered by a dog walking past, and gone at the first bark. The
 *   pigeon: proximity means nothing to it, noise means everything.
 *
 * The map from type to reaction is `HAZARD.REACTION`; the behaviour itself is
 * `entities/Hazard.ts`.
 */
export type CritterReaction = 'flee' | 'hold' | 'nerve' | 'flush';

/**
 * Why a run ended, so the loss overlay can say something true.
 *
 * - `car`    a vehicle touched the walker or the dog
 * - `frenzy` the frenzy meter filled: the dog got away
 */
export type LossReason = 'car' | 'frenzy';

/**
 * Tree silhouettes. Purely cosmetic — the shape a tree's foliage is stacked in.
 *
 * - `round`   a broad, low ball of leaves — the ordinary street tree
 * - `tall`    narrow and conical, stacked upward — a poplar or cypress
 * - `bushy`   wide, sprawling, many-lobed — a big old spreading oak
 *
 * Geometry per kind lives in systems/trees.ts; sizing multipliers in
 * `TREES.KINDS`.
 */
export type TreeKind = 'round' | 'tall' | 'bushy';

/**
 * What kind of body a road vehicle carries.
 *
 * - `car`  glass at both ends of the roof, a boot behind the cabin
 * - `van`  a cab at the front and a painted box behind it, glass only forward
 *
 * That difference is written for the plan view, which is what this camera
 * mostly gives you: a car shows a panel of paint with glass all round it, a van
 * shows a long roof with a band of glass at one end. Proportions live in
 * `VEHICLES.KINDS`, the bodies in render/models.ts.
 */
export type VehicleKind = 'car' | 'van';

/**
 * Events the game core emits and the HUD consumes. The HUD never reads gameplay
 * objects directly — it only reacts to these, which keeps simulation and
 * presentation decoupled.
 */
export const GameEvents = {
  DOG_STATE: 'dog-state', // (state: DogState)
  OBJECTIVE: 'objective', // (objective: Objective)
  /** (ratio: 0..1) — how far through the squat the dog is; 0 when it isn't going.
   *  The only meter the errand has now — there was a second one for the cleanup
   *  hold, the same shape in the same place a couple of seconds later, and two
   *  of them read as one meter that mysteriously restarted. */
  POOP_PROGRESS: 'poop-progress',
  /** (ratio: 0..1) — the frenzy meter. Fills with critter proximity; 1 ends the run. */
  FRENZY: 'frenzy',
  /**
   * (card: TutorialCard | null) — the guided walk's current lesson, or null
   * when there isn't one: an ordinary level, or a tutorial that has finished.
   * Only the ids travel; the wording is the HUD's, like every other line.
   */
  TUTORIAL: 'tutorial',
  WON: 'won', // ()
  LOST: 'lost', // (reason: LossReason) — run ended
  INPUT_SCHEME: 'input-scheme', // (scheme: InputScheme) — what the player is using
  SOUND_CRITTER_SPOTTED: 'sound-critter-spotted', // (type: HazardType)
  SOUND_HAZARD_STARTLE: 'sound-hazard-startle', // (type: HazardType)
  /** (intensity: 0..1) — the dog barking at whatever it is lunging for. */
  SOUND_DOG_BARK: 'sound-dog-bark',
  /** () — a vehicle bearing down on the walker or the dog. */
  SOUND_CAR_HORN: 'sound-car-horn',
  /** (pos: Vec2) — player position for HUD navigation pointers. */
  PLAYER_POS: 'player-pos',
  SOUND_TOGGLE_MUTE: 'sound-toggle-mute', // ()
} as const;

/**
 * Which control scheme the player is actually using, so on-screen hints can name
 * the right thing. Both schemes always work; this only decides wording.
 *
 * Starts as a guess from the device, then switches to whatever they genuinely
 * touch first — a device guess alone gets hybrids (laptop + touchscreen, tablet +
 * keyboard) wrong, and leaves the hint naming keys that nobody is pressing.
 */
export type InputScheme = 'keys' | 'pointer';

/** Minimal typed event emitter (replaces Phaser's scene event emitter). */
export class Emitter {
  private handlers = new Map<string, Set<(...args: never[]) => void>>();

  on(event: string, fn: (...args: never[]) => void): void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(fn);
  }

  off(event: string, fn: (...args: never[]) => void): void {
    this.handlers.get(event)?.delete(fn);
  }

  emit(event: string, ...args: unknown[]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const fn of set) (fn as (...a: unknown[]) => void)(...args);
  }

  removeAll(): void {
    this.handlers.clear();
  }
}
